/**
 * Backend Settings Service
 *
 * API client for the FastAPI backend settings endpoints.
 * Uses simplified X-User-Id authentication for Azure deployment.
 */

import { apiRequest } from '@/lib/api-client'
import { logError, logInfo } from '@/lib/logger'
import type { SettingsState } from '@/types/settings'
import { env } from '@/lib/env'

const BACKEND_URL = env.backendApiUrl

/**
 * Settings response from the backend API.
 */
interface SettingsApiResponse {
  id: string
  user_id: string
  settings_data: SettingsState
  chat_preferences: Record<string, unknown> | null
  created_at: string
  updated_at: string
  message?: string
}

/**
 * Backend Settings Service
 *
 * Provides methods to interact with the FastAPI backend for user settings.
 */
export const backendSettingsService = {
  /**
   * Load settings from the backend.
   * Returns null if no settings exist (404).
   */
  async loadSettings(): Promise<SettingsState | null> {
    try {
      const data = await apiRequest<SettingsApiResponse>('/settings')
      if (!data) {
        logInfo({ context: 'BackendSettingsService' }, 'No settings found in backend')
        return null
      }
      logInfo({ context: 'BackendSettingsService' }, 'Settings loaded from backend')
      return data.settings_data
    } catch (error) {
      logError(error as Error, { context: 'BackendSettingsService.loadSettings' })
      throw error
    }
  },

  /**
   * Save settings to the backend (full replace).
   * Creates settings if they don't exist.
   */
  async saveSettings(settings: SettingsState): Promise<boolean> {
    try {
      await apiRequest<SettingsApiResponse>('/settings', {
        method: 'PUT',
        body: JSON.stringify({ settings_data: settings }),
      })
      logInfo({ context: 'BackendSettingsService' }, 'Settings saved to backend')
      return true
    } catch (error) {
      logError(error as Error, { context: 'BackendSettingsService.saveSettings' })
      return false
    }
  },

  /**
   * Partially update settings (merge with existing).
   */
  async patchSettings(partialSettings: Partial<SettingsState>): Promise<boolean> {
    try {
      await apiRequest<SettingsApiResponse>('/settings', {
        method: 'PATCH',
        body: JSON.stringify({ settings_data: partialSettings }),
      })
      logInfo({ context: 'BackendSettingsService' }, 'Settings patched in backend')
      return true
    } catch (error) {
      logError(error as Error, { context: 'BackendSettingsService.patchSettings' })
      return false
    }
  },

  /**
   * Delete settings from the backend.
   */
  async deleteSettings(): Promise<boolean> {
    try {
      await apiRequest('/settings', { method: 'DELETE' })
      logInfo({ context: 'BackendSettingsService' }, 'Settings deleted from backend')
      return true
    } catch (error) {
      logError(error as Error, { context: 'BackendSettingsService.deleteSettings' })
      return false
    }
  },

  /**
   * Check if the backend is healthy/reachable.
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${BACKEND_URL}/health`)
      if (!response.ok) return false

      const data = await response.json()
      return data.status === 'healthy'
    } catch {
      return false
    }
  },
}
